/**
 * Utility functions for formatting time and date values
 */

/**
 * Formats a time string or Date object to 12-hour format (e.g., "2:30pm")
 * @param time - Time as Date object or ISO string
 * @returns Formatted time string in 12-hour format
 */
export const formatTime12Hour = (time: Date | string): string => {
  const date = typeof time === 'string' ? new Date(time) : time;
  
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).toLowerCase();
};

/**
 * Formats a time range from start to end time (e.g., "12:00pm - 1:30pm")
 * @param startTime - Start time as Date object or ISO string
 * @param endTime - End time as Date object or ISO string
 * @returns Formatted time range string
 */
export const formatTimeRange = (startTime: Date | string, endTime: Date | string): string => {
  const formattedStart = formatTime12Hour(startTime);
  const formattedEnd = formatTime12Hour(endTime);
  
  return `${formattedStart} - ${formattedEnd}`;
};

/**
 * Calculates end time based on start time and duration in minutes
 * @param startTime - Start time as Date object or ISO string
 * @param durationMinutes - Duration in minutes
 * @returns End time as Date object
 */
export const calculateEndTime = (startTime: Date | string, durationMinutes: number): Date => {
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = new Date(start.getTime() + (durationMinutes * 60 * 1000));
  return end;
};

/**
 * Formats a session time range using start time and duration
 * @param startTime - Start time as Date object or ISO string
 * @param durationMinutes - Duration in minutes
 * @returns Formatted time range string (e.g., "12:00pm - 1:30pm")
 */
export const formatSessionTimeRange = (startTime: Date | string, durationMinutes: number): string => {
  const endTime = calculateEndTime(startTime, durationMinutes);
  return formatTimeRange(startTime, endTime);
};

/**
 * Formats a local time string to 12-hour format (e.g., "2:30pm")
 * @param timeLocal - Time in HH:mm:ss or HH:mm format (e.g., "23:00:00", "7:00:00")
 * @returns Formatted time string in 12-hour format
 */
export const formatLocalTime12Hour = (timeLocal: string): string => {
  // Parse the time string (HH:mm:ss or HH:mm)
  const [hours, minutes] = timeLocal.split(':').map(Number);
  
  // Create a date object for today with the specified time
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).toLowerCase();
};

/**
 * Formats a local time range using start time and duration
 * @param timeLocal - Start time in HH:mm:ss or HH:mm format (e.g., "23:00:00", "7:00:00")
 * @param durationMinutes - Duration in minutes
 * @returns Formatted time range string (e.g., "7:00am - 8:00am")
 */
export const formatLocalTimeRange = (timeLocal: string, durationMinutes: number): string => {
  // Parse the start time
  const [hours, minutes] = timeLocal.split(':').map(Number);
  
  // Create start date
  const startDate = new Date();
  startDate.setHours(hours, minutes, 0, 0);
  
  // Calculate end time
  const endDate = new Date(startDate.getTime() + (durationMinutes * 60 * 1000));
  
  // Format both times
  const startTime = formatLocalTime12Hour(timeLocal);
  const endTime = endDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).toLowerCase();
  
  return `${startTime} - ${endTime}`;
};